import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Badge,
} from "@/components/ui";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { PageHeader } from "@/components/ui/page-header";
import { holidaysApi } from "@/services/api";
import {
  Calendar,
  Plus,
  Trash2,
  Upload,
  Loader2,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { formatDate } from "@/lib/utils";

interface Holiday {
  id: string;
  date: string;
  description: string;
  createdAt: string;
}

export function HolidaysSettingsPage() {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  // Filter state
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState<Holiday | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    date: "",
    description: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Import state
  const [importText, setImportText] = useState("");
  const [importResult, setImportResult] = useState<{
    imported: number;
    skipped: number;
    errors: string[];
  } | null>(null);

  // Result feedback
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  useEffect(() => {
    loadData();
  }, [selectedYear]);

  async function loadData() {
    setLoading(true);
    try {
      const data = await holidaysApi.getByYear(selectedYear);
      setHolidays(data);
    } catch (error) {
      console.error("Erro ao carregar feriados:", error);
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setFormData({ date: "", description: "" });
    setErrors({});
  }

  function openAddModal() {
    resetForm();
    setShowAddModal(true);
  }

  function openDeleteConfirm(holiday: Holiday) {
    setSelectedHoliday(holiday);
    setConfirmDelete(true);
  }

  function openImportModal() {
    setImportText("");
    setImportResult(null);
    setShowImportModal(true);
  }

  function validateForm(): boolean {
    const newErrors: Record<string, string> = {};

    if (!formData.date) {
      newErrors.date = "Data é obrigatória";
    }

    if (!formData.description || formData.description.trim().length < 2) {
      newErrors.description = "Descrição é obrigatória (mínimo 2 caracteres)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleAdd() {
    if (!validateForm()) return;

    setSaving(true);
    setResult(null);
    try {
      await holidaysApi.create({
        date: formData.date,
        description: formData.description.trim(),
      });
      setShowAddModal(false);
      resetForm();
      setResult({ success: true, message: "Feriado adicionado com sucesso!" });
      await loadData();
    } catch (error) {
      setResult({
        success: false,
        message: error instanceof Error ? error.message : "Erro ao adicionar feriado",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!selectedHoliday) return;

    setDeleting(true);
    try {
      await holidaysApi.delete(selectedHoliday.id);
      setConfirmDelete(false);
      setSelectedHoliday(null);
      setResult({ success: true, message: "Feriado removido com sucesso!" });
      await loadData();
    } catch (error) {
      setResult({
        success: false,
        message: error instanceof Error ? error.message : "Erro ao remover feriado",
      });
    } finally {
      setDeleting(false);
    }
  }

  async function handleImport() {
    if (!importText.trim()) return;

    setImporting(true);
    setImportResult(null);
    try {
      // Parse CSV format: date;description (one per line)
      const lines = importText.trim().split("\n");
      const holidays: { date: string; description: string }[] = [];

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        // Support both ; and , as separators
        const parts = trimmed.includes(";")
          ? trimmed.split(";")
          : trimmed.split(",");

        if (parts.length >= 2) {
          const dateStr = parts[0].trim();
          const description = parts.slice(1).join(",").trim();

          // Parse date (support dd/mm/yyyy or yyyy-mm-dd)
          let formattedDate = dateStr;
          if (dateStr.includes("/")) {
            const [day, month, year] = dateStr.split("/");
            formattedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
          }

          holidays.push({ date: formattedDate, description });
        }
      }

      if (holidays.length === 0) {
        setImportResult({
          imported: 0,
          skipped: 0,
          errors: ["Nenhum feriado válido encontrado no formato esperado"],
        });
        return;
      }

      const result = await holidaysApi.import(holidays);
      setImportResult(result);

      if (result.imported > 0) {
        await loadData();
      }
    } catch (error) {
      setImportResult({
        imported: 0,
        skipped: 0,
        errors: [error instanceof Error ? error.message : "Erro ao importar"],
      });
    } finally {
      setImporting(false);
    }
  }

  // Generate year options (current year +/- 2 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <PageHeader
          icon={<Calendar className="w-5 h-5 sm:w-6 sm:h-6" />}
          title="Feriados Bancários"
          description="Calendário de feriados para cálculo de dias úteis"
        />
        <div className="flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Calendar className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Feriados Bancarios"
        description="Calendario de feriados para calculo de dias uteis"
        stats={[
          { value: holidays.length, label: `Feriados ${selectedYear}` },
        ]}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={openImportModal}>
              <Upload className="w-4 h-4 mr-2" />
              Importar
            </Button>
            <Button onClick={openAddModal}>
              <Plus className="w-4 h-4 mr-2" />
              Novo Feriado
            </Button>
          </div>
        }
      />

      {/* Year Filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium">Ano:</label>
            <select
              className="h-10 px-3 rounded-md border border-input bg-background text-sm"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <Badge variant="secondary">{holidays.length} feriados</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Result feedback */}
      {result && (
        <div
          className={`p-4 rounded-lg flex items-center gap-3 ${
            result.success
              ? "bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {result.success ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <XCircle className="w-5 h-5" />
          )}
          <span>{result.message}</span>
        </div>
      )}

      {/* Holidays Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Feriados de {selectedYear}</CardTitle>
        </CardHeader>
        <CardContent>
          {holidays.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhum feriado cadastrado para {selectedYear}.
            </p>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-4 font-medium text-sm">Data</th>
                    <th className="text-left p-4 font-medium text-sm">
                      Descrição
                    </th>
                    <th className="text-right p-4 font-medium text-sm">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {holidays.map((holiday) => (
                    <tr key={holiday.id} className="hover:bg-muted/50">
                      <td className="p-4 text-sm font-medium">
                        {formatDate(holiday.date)}
                      </td>
                      <td className="p-4 text-sm">{holiday.description}</td>
                      <td className="p-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteConfirm(holiday)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Novo Feriado</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Data</label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) =>
                    setFormData({ ...formData, date: e.target.value })
                  }
                  error={errors.date}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Descrição</label>
                <Input
                  placeholder="Ex: Carnaval"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  error={errors.description}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancelar
                </Button>
                <Button onClick={handleAdd} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Adicionar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader>
              <CardTitle>Importar Feriados</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">
                  Cole a lista de feriados
                </label>
                <textarea
                  className="w-full h-48 p-3 rounded-md border border-input bg-background text-sm font-mono resize-none"
                  placeholder={`Formato: data;descrição (uma por linha)
Exemplos:
01/01/2025;Confraternização Universal
21/04/2025;Tiradentes
01/05/2025;Dia do Trabalho

ou

2025-01-01;Confraternização Universal
2025-04-21;Tiradentes`}
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                />
              </div>

              {importResult && (
                <div
                  className={`p-4 rounded-lg ${
                    importResult.imported > 0
                      ? "bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                      : "bg-yellow-50 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400"
                  }`}
                >
                  <p className="font-medium">Resultado da importação:</p>
                  <ul className="text-sm mt-1">
                    <li>Importados: {importResult.imported}</li>
                    <li>Ignorados (duplicados): {importResult.skipped}</li>
                    {importResult.errors.length > 0 && (
                      <li className="text-red-600 dark:text-red-400">
                        Erros: {importResult.errors.join(", ")}
                      </li>
                    )}
                  </ul>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowImportModal(false)}
                >
                  {importResult?.imported ? "Fechar" : "Cancelar"}
                </Button>
                {!importResult?.imported && (
                  <Button
                    onClick={handleImport}
                    disabled={importing || !importText.trim()}
                  >
                    {importing && (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    )}
                    Importar
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => {
          setConfirmDelete(false);
          setSelectedHoliday(null);
        }}
        onConfirm={handleDelete}
        title="Excluir Feriado"
        description={`Tem certeza que deseja excluir o feriado "${
          selectedHoliday?.description || ""
        }" (${
          selectedHoliday ? formatDate(selectedHoliday.date) : ""
        })? Esta ação não pode ser desfeita.`}
        variant="danger"
        loading={deleting}
      />
    </div>
  );
}
